import React, { useState } from "react";
import {
  Eye,
  EyeOff,
  Loader2,
  ChevronDown,
  X,
  AlertTriangle,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useSignupAthlete } from "../../hooks/queries/useAuthMutations";
import PolicyModal from "./PolicyModal";
import ExpandableText from "./ExpandableText";
import { TERMS_AND_CONDITIONS, PRIVACY_POLICY, DOCUMENT_VERSION } from "../../data/legalDocuments";
import { FOOTBALL_SPORT_LABEL } from "../../constants/footballTaxonomy";

const COUNTRIES = [
  "Argentina",
  "Bolivia",
  "Brasil",
  "Chile",
  "Colombia",
  "Costa Rica",
  "Cuba",
  "Ecuador",
  "El Salvador",
  "España",
  "Estados Unidos",
  "Guatemala",
  "Honduras",
  "México",
  "Nicaragua",
  "Panamá",
  "Paraguay",
  "Perú",
  "Puerto Rico",
  "República Dominicana",
  "Uruguay",
  "Venezuela",
];

/* ───────────────────────────────────────────
   Error Pop-up
   ─────────────────────────────────────────── */
const ErrorPopup = ({ isOpen, message, onClose }) => (
  <AnimatePresence>
    {isOpen && (
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-xl w-full max-w-sm mx-4 p-8 relative"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
        >
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
          >
            <X size={20} />
          </button>

          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle size={28} className="text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">
              Error en el registro
            </h3>
            <p className="text-sm text-gray-600 mb-6 leading-relaxed">
              {message}
            </p>
            <button
              onClick={onClose}
              className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3 rounded-lg transition-colors duration-200"
            >
              Entendido
            </button>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

/* ───────────────────────────────────────────
   Guardian Pop-up (for minors < 18)
   ─────────────────────────────────────────── */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const GuardianPopup = ({ isOpen, onClose, onSubmit, athleteEmail }) => {
  const [guardianName, setGuardianName] = useState("");
  const [guardianEmail, setGuardianEmail] = useState("");
  const [guardianEmailConfirm, setGuardianEmailConfirm] = useState("");
  const [certified, setCertified] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = () => {
    setError("");
    if (!guardianName.trim()) {
      setError("El nombre del tutor es obligatorio.");
      return;
    }
    if (!guardianEmail || !guardianEmailConfirm) {
      setError("Ambos campos de correo son obligatorios.");
      return;
    }
    const normalizedEmail = guardianEmail.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(normalizedEmail)) {
      setError("Revisa el correo del tutor: no parece un correo válido.");
      return;
    }
    if (normalizedEmail !== guardianEmailConfirm.trim().toLowerCase()) {
      setError("Los correos no coinciden.");
      return;
    }
    if (normalizedEmail === athleteEmail.trim().toLowerCase()) {
      setError("El correo del tutor debe ser distinto al tuyo. Usa el correo de tu papá, mamá o representante.");
      return;
    }
    if (!certified) {
      setError("Debes certificar que la información es verdadera.");
      return;
    }
    onSubmit(normalizedEmail, guardianName.trim());
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className="bg-white rounded-2xl shadow-xl w-full max-w-lg mx-4 p-6 sm:p-8 relative max-h-[90vh] overflow-y-auto"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Close */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 transition-colors"
            >
              <X size={24} />
            </button>

            {/* Content */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-extrabold text-gray-900 uppercase tracking-wide mb-2">
                ¡Tu talento necesita un respaldo!
              </h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                Como eres menor de edad, necesitamos{" "}
                <span className="font-semibold text-gray-800">
                  el contacto de tu tutor legal para que sigas con tu registro.
                </span>
              </p>
            </div>

            <div className="mb-5 rounded-lg bg-blue-50 p-4 text-left text-sm text-blue-950">
              <p className="font-semibold">¿Para qué usamos este correo?</p>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-blue-900">
                <li>Le avisaremos a tu tutor que creaste tu cuenta.</li>
                <li>
                  Cuando un scout quiera contactarte, le enviaremos la solicitud a tu
                  tutor. Solo podrás hablar con el scout si tu tutor la aprueba.
                </li>
              </ul>
              <p className="mt-2 text-blue-900">
                Usa un correo que tu tutor revise seguido.
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Fields */}
            <div className="space-y-4 mb-4">
              <input
                type="text"
                placeholder="Nombre completo del tutor legal"
                value={guardianName}
                onChange={(e) => setGuardianName(e.target.value)}
                className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
              />
              <input
                type="email"
                placeholder="Correo del tutor"
                value={guardianEmail}
                onChange={(e) => setGuardianEmail(e.target.value)}
                className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
              />
              <input
                type="email"
                placeholder="Confirmación de correo"
                value={guardianEmailConfirm}
                onChange={(e) => setGuardianEmailConfirm(e.target.value)}
                className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
              />
            </div>

            <label className="flex items-start gap-2.5 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={certified}
                onChange={(e) => setCertified(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-950 focus:ring-blue-900"
              />
              <span className="text-xs text-gray-600 leading-relaxed">
                Certifico que la información de mi tutor legal es verdadera, precisa y actualizada.
              </span>
            </label>

            <p className="text-xs text-gray-500 mb-6">
              *Tu tutor legal puede ser tu papá, mamá o un representante
              autorizado.
            </p>

            {/* Submit */}
            <div className="flex justify-center">
              <button
                onClick={handleSubmit}
                className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3"
              >
                Continuar
                <span className="text-base">✦</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

/* ───────────────────────────────────────────
   Step Progress Bar
   ─────────────────────────────────────────── */
const StepProgressBar = ({ currentStep, totalSteps }) => (
  <div className="w-full flex gap-1 mb-6">
    {Array.from({ length: totalSteps }).map((_, i) => (
      <div
        key={i}
        className={`flex-1 h-1.5 rounded-full transition-colors duration-300 ${
          i <= currentStep ? "bg-blue-950" : "bg-gray-200"
        }`}
      />
    ))}
  </div>
);

/* ───────────────────────────────────────────
   Country Select Dropdown
   ─────────────────────────────────────────── */
const CountrySelect = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-left flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
      >
        <span className={value ? "text-gray-900" : "text-gray-400"}>
          {value || "País de la residencia*"}
        </span>
        <ChevronDown
          size={18}
          className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
        />
      </button>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {COUNTRIES.map((country) => (
            <button
              key={country}
              type="button"
              onClick={() => {
                onChange(country);
                setIsOpen(false);
              }}
              className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {country}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/* ───────────────────────────────────────────
   Main: AthleteSignUpPage
   ─────────────────────────────────────────── */
const AthleteSignUpPage = ({
  onSignUpComplete,
  onGoToLogin,
  onChangeAccountType,
}) => {
  const [step, setStep] = useState(0); // 0 = Step 1, 1 = Step 2

  // Step 1 fields
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [country, setCountry] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);

  // Step 2 fields
  const [fullName, setFullName] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);

  // Policy modals
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  // Guardian popup
  const [showGuardianPopup, setShowGuardianPopup] = useState(false);
  const [guardianEmail, setGuardianEmail] = useState("");
  const [guardianName, setGuardianName] = useState("");

  // General
  const [error, setError] = useState("");
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [emailTaken, setEmailTaken] = useState(false);
  const signup = useSignupAthlete();
  const isLoading = signup.isPending;

  /* ─── Age check helper ─── */
  const getAge = (dateStr) => {
    const today = new Date();
    const birth = new Date(dateStr);
    let age = today.getFullYear() - birth.getFullYear();
    const m = today.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  /* ─── Step 1 validation ─── */
  const handleContinue = async (e) => {
    e.preventDefault();
    setError("");

    if (!email || !password || !passwordConfirm || !country) {
      setError("Todos los campos son obligatorios.");
      return;
    }
    if (emailTaken) {
      setError("Este correo ya está registrado. Intenta iniciar sesión.");
      return;
    }
    if (password.length < 8) {
      setError("La contraseña debe tener al menos 8 caracteres.");
      return;
    }
    if (password !== passwordConfirm) {
      setError("Las contraseñas no coinciden.");
      return;
    }

    setStep(1);
  };

  /* ─── Step 2 / Final submit ─── */
  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError("");

    if (!fullName || !birthDate) {
      setError("Todos los campos son obligatorios.");
      setShowErrorPopup(true);
      return;
    }
    if (!acceptTerms || !acceptPrivacy) {
      setError("Debes aceptar los términos y la política de privacidad.");
      setShowErrorPopup(true);
      return;
    }

    // Check age — if minor show popup first
    const age = getAge(birthDate);
    if (age < 18 && !guardianEmail) {
      setShowGuardianPopup(true);
      return;
    }

    // Consent timestamps at the moment of registration (Ley 1581);
    // the backend stores them atomically under attributes.consents.
    const consentTimestamp = new Date().toISOString();
    const consents = {
      terms_accepted_at: acceptTerms ? consentTimestamp : null,
      terms_doc_version: DOCUMENT_VERSION,
      privacy_accepted_at: acceptPrivacy ? consentTimestamp : null,
      privacy_doc_version: DOCUMENT_VERSION,
    };

    try {
      const data = await signup.mutateAsync({
        email,
        password,
        fullName,
        sport: FOOTBALL_SPORT_LABEL,
        birthDate,
        country,
        guardianEmail: guardianEmail || undefined,
        guardianName: guardianName || undefined,
        consents,
      });

      // Success — response carries the created athlete row as `profile`
      if (onSignUpComplete) onSignUpComplete(data);
    } catch (err) {
      if (err?.code === "email_taken") {
        setEmailTaken(true);
        setError("Este correo ya está registrado. Intenta iniciar sesión.");
      } else if (err?.code === "rate_limited") {
        setError(
          "Has realizado demasiados intentos. Espera un momento antes de intentar de nuevo.",
        );
      } else if (err?.code === "API_UNREACHABLE") {
        setError("Error de conexión. Inténtalo de nuevo.");
      } else {
        setError(err?.message || "Error de conexión. Inténtalo de nuevo.");
      }
      setShowErrorPopup(true);
    }
  };

  /* ─── Guardian callback ─── */
  const handleGuardianSubmit = (email, name) => {
    setGuardianEmail(email);
    setGuardianName(name || "");
    setShowGuardianPopup(false);
    // After guardian info provided, immediately try to create account
    // We use a small timeout so state updates propagate
    setTimeout(() => {
      document.getElementById("signup-form")?.requestSubmit();
    }, 100);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white overflow-x-hidden">
      {/* ─── Main Content ─── */}
      <div className="flex-1 flex items-start sm:items-center justify-center px-4 py-8 sm:py-12">
        <div className="w-full max-w-md">
          {/* Step Progress */}
          <StepProgressBar currentStep={step} totalSteps={2} />

          <AnimatePresence mode="wait">
            {/* ═══════════════════════════════════════
                STEP 1
               ═══════════════════════════════════════ */}
            {step === 0 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide italic mb-6">
                  Paso 1
                </h2>

                {/* Error */}
                {error && step === 0 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}

                <form onSubmit={handleContinue} className="space-y-4">
                  {/* Email */}
                  <div>
                    <input
                      type="email"
                      placeholder="Correo electrónico*"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailTaken(false);
                      }}
                      className={`w-full px-4 py-3.5 bg-gray-100 border rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all ${
                        emailTaken ? "border-red-400" : "border-gray-200"
                      }`}
                      required
                    />
                    {emailTaken && (
                      <p className="mt-1 text-xs text-red-500">
                        Este correo ya está registrado.
                      </p>
                    )}
                  </div>

                  {/* Password */}
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Contraseña*"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>

                  {/* Password Confirm */}
                  <div className="relative">
                    <input
                      type={showPasswordConfirm ? "text" : "password"}
                      placeholder="Confirmación de contraseña*"
                      value={passwordConfirm}
                      onChange={(e) => setPasswordConfirm(e.target.value)}
                      className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowPasswordConfirm(!showPasswordConfirm)
                      }
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {showPasswordConfirm ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </button>
                  </div>

                  {/* Country */}
                  <CountrySelect value={country} onChange={setCountry} />

                  {/* Continue */}
                  <div className="flex justify-center pt-4">
                    <button
                      type="submit"
                      className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3"
                    >
                      Continuar
                      <span className="text-base">✦</span>
                    </button>
                  </div>
                </form>
              </motion.div>
            )}

            {/* ═══════════════════════════════════════
                STEP 2
               ═══════════════════════════════════════ */}
            {step === 1 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.3 }}
              >
                <h2 className="text-2xl font-bold text-gray-900 uppercase tracking-wide italic mb-6">
                  Paso 2
                </h2>

                {/* Error */}
                {error && step === 1 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                    {error}
                  </div>
                )}

                <form
                  id="signup-form"
                  onSubmit={handleCreateAccount}
                  className="space-y-4"
                >
                  {/* Full Name */}
                  <input
                    type="text"
                    placeholder="Nombre completo*"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all"
                    required
                  />

                  {/* Birth Date */}
                  <div className="relative">
                    <input
                      type="date"
                      value={birthDate}
                      onChange={(e) => setBirthDate(e.target.value)}
                      className={`block w-full min-w-0 min-h-[3.25rem] appearance-none px-4 py-3.5 bg-gray-100 border border-gray-200 rounded-lg text-base text-left focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-transparent transition-all ${
                        birthDate ? "text-gray-900" : "text-transparent"
                      }`}
                      required
                    />
                    {!birthDate && (
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-base text-gray-400 pointer-events-none">
                        Fecha de nacimiento*
                      </span>
                    )}
                  </div>

                  {/* Terms notice + checkbox */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                    <ExpandableText
                      text={TERMS_AND_CONDITIONS.summary}
                      className="text-xs text-gray-600 leading-relaxed"
                    />
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-xs font-semibold text-blue-900 hover:underline underline-offset-2 transition-colors"
                    >
                      Ver Términos y Condiciones completos →
                    </button>
                    <label className="flex items-center gap-3 cursor-pointer select-none py-2 w-full touch-manipulation">
                      <input
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="w-4 h-4 shrink-0 rounded border-gray-300 accent-blue-950 focus:ring-blue-900 cursor-pointer"
                      />
                      <span className="text-sm text-gray-800 font-medium">
                        Acepto los Términos y Condiciones
                      </span>
                    </label>
                  </div>

                  {/* Privacy notice + checkbox */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                    <ExpandableText
                      text={PRIVACY_POLICY.summary}
                      className="text-xs text-gray-600 leading-relaxed"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-xs font-semibold text-blue-900 hover:underline underline-offset-2 transition-colors"
                    >
                      Ver Política de Privacidad completa →
                    </button>
                    <label className="flex items-center gap-3 cursor-pointer select-none py-2 w-full touch-manipulation">
                      <input
                        type="checkbox"
                        checked={acceptPrivacy}
                        onChange={(e) => setAcceptPrivacy(e.target.checked)}
                        className="w-4 h-4 shrink-0 rounded border-gray-300 accent-blue-950 focus:ring-blue-900 cursor-pointer"
                      />
                      <span className="text-sm text-gray-800 font-medium">
                        Autorizo el tratamiento de mis datos personales
                      </span>
                    </label>
                  </div>

                  {/* Create Account */}
                  <div className="flex justify-center pt-4">
                    <button
                      type="submit"
                      disabled={isLoading}
                      className="bg-blue-950 hover:bg-blue-900 text-white text-xs font-bold tracking-[0.2em] uppercase px-8 py-3.5 rounded-lg transition-colors duration-200 flex items-center gap-3 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {isLoading ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <>
                          Crear cuenta
                          <span className="text-base">✦</span>
                        </>
                      )}
                    </button>
                  </div>
                </form>

                {/* Back to step 1 */}
                <button
                  onClick={() => {
                    setError("");
                    setStep(0);
                  }}
                  className="w-full text-center mt-4 text-sm text-gray-500 hover:text-blue-900 transition-colors"
                >
                  ← Volver al paso anterior
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Already have account */}
          <button
            type="button"
            onClick={onChangeAccountType}
            className="mt-6 w-full text-center text-sm text-gray-500 transition-colors hover:text-blue-900"
          >
            Cambiar tipo de cuenta
          </button>
          <p className="mt-3 text-center text-sm text-gray-500">
            ¿Ya tienes cuenta?{" "}
            <button
              onClick={onGoToLogin}
              className="text-gray-700 font-medium hover:text-blue-900 underline underline-offset-2 transition-colors"
            >
              Inicia sesión
            </button>
          </p>
        </div>
      </div>

      {/* Policy Modals */}
      <PolicyModal
        isOpen={showTermsModal}
        document={TERMS_AND_CONDITIONS}
        onClose={() => setShowTermsModal(false)}
      />
      <PolicyModal
        isOpen={showPrivacyModal}
        document={PRIVACY_POLICY}
        onClose={() => setShowPrivacyModal(false)}
      />

      {/* Guardian Popup */}
      <GuardianPopup
        isOpen={showGuardianPopup}
        onClose={() => setShowGuardianPopup(false)}
        athleteEmail={email}
        onSubmit={handleGuardianSubmit}
      />

      {/* Error Popup */}
      <ErrorPopup
        isOpen={showErrorPopup}
        message={error}
        onClose={() => setShowErrorPopup(false)}
      />
    </div>
  );
};

export default AthleteSignUpPage;
